import { useRef } from 'react';

import { useEntitlements, useMe } from '@/auth/queries';
import { AVATARES } from '@/lib/avatares';
import { fechaLarga, mxn } from '@/lib/formato';
import { useStagger } from '@/lib/motion';
import { tratamiento } from '@/lib/utils';

import { useMisLicencias, useTienda } from './queries';
import { TarjetaMembresia } from './TarjetaMembresia';

export function LicenciasPage() {
  const tienda = useTienda();
  const mias = useMisLicencias();
  const { data: ent } = useEntitlements();
  const { data: me } = useMe();
  const root = useRef<HTMLDivElement>(null);
  // Las licencias vigentes (compradas u obsequiadas) ya no se ofrecen en la tienda.
  const vigentes = new Set(
    mias.data?.filter((l) => l.active && !l.refunded).map((l) => l.membershipId),
  );
  const comprables = mias.isPending ? undefined : tienda.data?.filter((m) => !vigentes.has(m.id));
  useStagger(root, '[data-card]', { step: 70, deps: [comprables?.length] });

  return (
    <div ref={root} className="space-y-10">
      <section>
        <h1 className="titulo-seccion">
          <span className="subrayado-verde">Licencias</span>
        </h1>
        {/* Aviso con degradado de marca y saludo, como el de la tienda del legacy. */}
        <div className="relative mt-4 overflow-hidden rounded-[var(--radius-card)] bg-marca px-5 py-4 text-white shadow-[var(--shadow-vidrio)] sm:pr-28">
          {me && (
            <p className="font-extrabold tracking-wide uppercase">
              {tratamiento(me.nickname || me.name, me.gender)}
            </p>
          )}
          <p className="mt-1 text-white/95">
            Adquiere alguna de las siguientes licencias y disfruta del{' '}
            <strong>contenido exclusivo</strong> para tu preparación en el{' '}
            <strong>ENARM {ent?.enarmYear ?? ''}</strong>.
          </p>
          <img
            src={me?.gender === 'H' ? AVATARES.doctorSenala : AVATARES.doctoraSenala}
            alt=""
            className="pointer-events-none absolute -bottom-8 right-4 hidden h-28 w-auto sm:block"
          />
        </div>
        {tienda.isError && (
          <p className="mt-6 text-rojo-texto">No pudimos cargar las licencias. Intenta de nuevo.</p>
        )}
        {comprables?.length === 0 && tienda.data && tienda.data.length > 0 && (
          <p className="mt-6 text-texto-2">
            Ya cuentas con todas las licencias disponibles. ¡Mucho éxito en tu preparación!
          </p>
        )}
        <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {comprables?.map((m) => (
            <TarjetaMembresia key={m.id} m={m} />
          ))}
          {(tienda.isPending || mias.isPending) &&
            [0, 1, 2].map((i) => (
              <div key={i} className="card-vidrio h-72 animate-pulse" aria-hidden />
            ))}
        </div>
      </section>

      <section className="card-cerebro">
        <img
          src="/img/isotipo-cerebro-blanco.png"
          alt=""
          className="card-cerebro__isotipo solo-oscuro"
        />
        <img
          src="/img/isotipo-cerebro-azul.png"
          alt=""
          className="card-cerebro__isotipo solo-claro"
        />
        <h2 className="text-center text-2xl font-extrabold text-azul-texto">Mis licencias</h2>
        {mias.data && mias.data.length === 0 && (
          <p className="mt-4 text-center text-texto-2">
            Aún no tienes licencias. Elige una arriba para comenzar.
          </p>
        )}
        {mias.data && mias.data.length > 0 && (
          <div className="mt-5 overflow-x-auto">
            <table className="tabla-cs tabla-cs-verde">
              <thead>
                <tr>
                  <th>Licencia</th>
                  <th>Vigente desde</th>
                  <th>Hasta</th>
                  <th>Status</th>
                  <th>Tu pago</th>
                  <th>Forma de pago</th>
                </tr>
              </thead>
              <tbody>
                {mias.data.map((l) => (
                  <tr
                    key={`${l.folio}-${String(l.membershipId)}`}
                    {...(l.active ? { 'data-destacada': '' } : {})}
                  >
                    <td>
                      {l.name}
                      {l.gift && <span className="insignia insignia-activa ml-2">Obsequio</span>}
                    </td>
                    <td>{fechaLarga(l.startsOn)}</td>
                    <td>{fechaLarga(l.endsOn)}</td>
                    <td>
                      {l.refunded ? (
                        <span className="font-bold text-rojo-texto">REEMBOLSADA</span>
                      ) : l.active ? (
                        <span className="font-extrabold text-verde-texto">ACTIVA</span>
                      ) : (
                        'INACTIVA'
                      )}
                    </td>
                    <td className="tabular">{l.amount ? mxn(l.amount) : '—'}</td>
                    <td>{l.method ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
